import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGames, useUsers } from '@/hooks/useShared';
import { calculateLeaderboard } from '@/services/leaderboard';
import { InsightsSubNav } from '@/components/insights/InsightsSubNav';
import { calculateAggregateStats } from '@/services/insights';
import { Card } from '@/components/ui';
import { formatCurrency, formatCurrencyShort } from '@/lib/utils';
import type { LeaderboardEntry } from '@/lib/types';

export function LeaderboardPage() {
  const { games } = useGames();
  const { users } = useUsers();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'wins' | 'losses'>('all');
  const [dateRange, setDateRange] = useState<'all' | '30' | '90' | '365'>('all');

  const dateFilter = useMemo(() => {
    if (dateRange === 'all') return undefined;
    const days = parseInt(dateRange);
    const start = new Date();
    start.setDate(start.getDate() - days);
    return { startDate: start };
  }, [dateRange]);

  const entries = useMemo(
    () => calculateLeaderboard(users, games, dateFilter),
    [users, games, dateFilter],
  );

  const aggregateStats = useMemo(() => calculateAggregateStats(games), [games]);

  const displayEntries = filter === 'wins'
    ? entries.filter(e => e.wins > 0)
    : filter === 'losses'
    ? entries.filter(e => e.losses > 0)
    : entries;

  return (
    <div className="max-w-[400px] mx-auto">
      <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
        <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">STATISTICS</div>
        <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Leaderboard</h1>
      </div>

      {/* Aggregate stat bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <StatCard label="Games" value={String(aggregateStats.totalGames)} />
        <StatCard label="Total Wagered" value={formatCurrencyShort(aggregateStats.totalWagered)} />
        <StatCard label="Biggest Pot" value={formatCurrencyShort(aggregateStats.biggestPot)} />
        <StatCard label="Avg Players" value={String(aggregateStats.avgPlayersPerGame)} />
      </div>

      <InsightsSubNav />

      {/* Date filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {(['all', '30', '90', '365'] as const).map(d => (
          <button
            key={d}
            onClick={() => setDateRange(d)}
            className={`px-[12px] py-[6px] rounded-full border-none cursor-pointer text-[11px] font-bold transition-colors ${
              dateRange === d
                ? 'bg-brand-primary text-bg-app'
                : 'bg-[#e8f5f0] text-text-muted hover:bg-[#d4e8e0]'
            }`}
          >
            {d === 'all' ? 'All Time' : `${d}d`}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-[20px]">
        {(['all', 'wins', 'losses'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-[16px] py-[8px] rounded-full border-none cursor-pointer text-[13px] font-bold transition-colors ${
              filter === f
                ? 'bg-brand-primary text-bg-app'
                : 'bg-[#e8f5f0] text-text-muted hover:bg-[#d4e8e0]'
            }`}
          >
            {f === 'all' ? 'All Time' : f === 'wins' ? 'Winners' : 'Wall of Shame'}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {displayEntries.map((entry, idx) => (
          <LeaderboardRow key={entry.user_id} entry={entry} rank={idx + 1} onViewProfile={() => navigate(`/insights/players/${entry.user_id}`)} />
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ flex: 1, background: '#e8f5f0', borderRadius: 12, padding: '10px 6px', textAlign: 'center' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: '#006855', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#2c2f30', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function LeaderboardRow({ entry, rank, onViewProfile }: { entry: LeaderboardEntry; rank: number; onViewProfile: () => void }) {
  const [expanded, setExpanded] = useState(false);

  const rankColorClass = rank === 1 ? 'text-brand-primary' : rank === 2 ? 'text-[#C0C0C0]' : rank === 3 ? 'text-[#CD7F32]' : 'text-text-muted';

  return (
    <Card className="mb-2 p-0 overflow-hidden">
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 p-[14px] cursor-pointer hover:bg-bg-app transition-colors"
      >
        <div className={`w-[28px] text-[16px] font-extrabold text-center ${rankColorClass}`}>
          {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : `#${rank}`}
        </div>

        <div className="w-9 h-9 rounded-full bg-brand-primary overflow-hidden flex-shrink-0">
          {entry.photo_url ? (
            <img src={entry.photo_url} alt={entry.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[16px] font-extrabold text-bg-app">
              {entry.name.charAt(0)}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-semibold text-text-main text-[15px] truncate">{entry.name}</div>
          <div className="text-[12px] text-text-muted">
            {entry.games_played} games
            {entry.current_streak > 0 && (
              <span className={`ml-1.5 ${entry.streak_type === 'win' ? 'text-status-success' : 'text-status-error'}`}>
                {entry.streak_type === 'win' ? '🔥' : '📉'} {entry.current_streak}
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className={`text-[18px] font-extrabold ${entry.total_pnl >= 0 ? 'text-status-success' : 'text-status-error'}`}>
            {entry.total_pnl >= 0 ? '+' : ''}{formatCurrencyShort(entry.total_pnl)}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-[14px] pb-[14px] border-t border-border-subtle">
          <div className="grid grid-cols-3 gap-2 mt-3">
            <Stat label="Best Night" value={formatCurrency(entry.best_night)} colorClass="text-status-success" />
            <Stat label="Worst Night" value={formatCurrency(entry.worst_night)} colorClass="text-status-error" />
            <Stat label="ROI" value={`${entry.roi >= 0 ? '+' : ''}${entry.roi}%`} colorClass={entry.roi >= 0 ? 'text-status-success' : 'text-status-error'} />
            <Stat label="Win Streak" value={String(entry.longestWinStreak)} colorClass="text-status-success" />
            <Stat label="Loss Streak" value={String(entry.longestLossStreak)} colorClass="text-status-error" />
            <Stat label="Consistency" value={entry.consistency.charAt(0).toUpperCase() + entry.consistency.slice(1)} colorClass="text-text-muted" />
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onViewProfile(); }}
            className="w-full mt-3 py-2 text-[11px] font-bold text-[#006855] bg-[#e8f5f0] rounded-xl border-none cursor-pointer hover:bg-[#d0ebe0] transition-colors"
          >
            View Full Profile →
          </button>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, colorClass }: { label: string; value: string; colorClass: string }) {
  return (
    <div className="bg-bg-app rounded-lg px-[10px] py-2">
      <div className="text-[10px] text-text-muted uppercase tracking-wider">{label}</div>
      <div className={`text-[15px] font-bold mt-0.5 ${colorClass}`}>{value}</div>
    </div>
  );
}
